import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User, ArrowRight, BookOpen, Tag, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const filteredPosts = activeCategory === 'All' ? posts : posts.filter(p => p.category === activeCategory);
  const allCategories = ['All', ...new Set(posts.map(p => p.category || 'General'))];

  return (
    <div className="min-h-screen" data-testid="blog-page">
      <SEO
        title="Health Blog"
        description="Health tips and news from K&K Medical and Dental. Stay informed about oral health, chronic disease management, pregnancy care, and wellness advice."
        keywords="health blog, medical advice, dental tips, chronic disease management, wellness tips South Africa"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-50 to-teal-50/30 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 rounded-full bg-teal-100 text-teal-700 text-sm font-semibold mb-6">
            Health & Wellness
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-6">
            Our Blog
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Stay informed with the latest health tips, news, and updates from K&K Medical and Dental.
          </p>
        </div>
      </section>

      {/* Posts */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="blog-posts-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600 mb-2">Latest Articles</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Health Tips & News</h2>
          </div>

          {/* Category Filter */}
          {posts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="category-filter">
              {allCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat
                      ? 'bg-teal-600 text-white shadow-md'
                      : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                  }`}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Post Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="border-0 shadow-lg rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <CardContent className="p-6">
                    <div className="h-4 bg-slate-200 rounded w-1/3 mb-4" />
                    <div className="h-6 bg-slate-200 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <Card
                  key={post.id}
                  className="border-0 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`blog-card-${post.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image_url || 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800'}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-teal-600/90 text-white text-xs font-medium">
                        {post.category || 'General'}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 flex items-center gap-3 text-white/90 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
                      <User className="w-4 h-4" />
                      <span>{post.author}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {post.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center gap-2 text-teal-600 font-medium text-sm hover:gap-3 transition-all duration-200"
                      data-testid={`read-more-${post.id}`}
                    >
                      Read more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="bg-slate-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                {activeCategory !== 'All' ? `No posts in "${activeCategory}"` : 'No Posts Yet'}
              </h3>
              <p className="text-slate-600 mb-6">
                Check back soon for health tips and news.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-white" data-testid="blog-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl p-8 lg:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Have Questions About Your Health?</h2>
            <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
              Our team is here to help. Contact us for professional medical advice and care.
            </p>
            <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 py-6 text-lg shadow-lg" data-testid="blog-contact-btn">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
